/**
 * Auto-redirect-and-capture for WorkOS OAuth.
 *
 * Runs once at module load time, before React mounts. Two cases:
 *
 *   1. URL contains `?code=…` — the user just came back from WorkOS.
 *      POST it to `VITE_AUTH_PROXY_URL` (the API gateway's
 *      `/v1/auth/exchange`), store the resulting access + refresh
 *      tokens in localStorage under origin/dashboard's key names,
 *      then strip the code from the URL.
 *
 *   2. No code, no `auth_token` in localStorage, AUTH_REQUIRED is on
 *      — bounce the user to the WorkOS authorize URL. They'll come
 *      back with `?code=…` and we hit case 1.
 *
 * If `AUTH_REQUIRED` is off (default `development` mode), the whole
 * thing is a no-op.
 *
 * Mirrors the relevant pieces of origin/dashboard's ApiContext.jsx
 * (the OAuth-callback effect at L127 and the login() at L455). We
 * intentionally do not re-implement the full auth context — refresh
 * still goes through authBridge.attemptRefresh() on 401, and we
 * don't need any of the WorkOS multi-org / password-grant flows.
 */

import { API_BASE_URL, AUTH_REQUIRED, selfHostedLogin } from "./featureFlags.js";
import { hasAccessToken, setTokens } from "./authTokenStore.js";

export const AUTH_API_BASE = API_BASE_URL.replace(/\/$/, "");

// Single-use code guard. WorkOS / proxy reject the second exchange
// of the same code; in StrictMode the bootstrap module imports
// twice. Using sessionStorage so a hard refresh resets it.
const CODE_PROCESSED_KEY = "dashboard:auth-code-processed";

// All upstream URLs come from the active env file. The proxy URL is
// preserved as informational (telemetry / debug) — actual exchange
// requests go through the relative `/v1/auth/exchange` path so
// Vite's dev proxy bridges to the configured gateway (avoids CORS).
const ENV = (typeof import.meta !== "undefined" && import.meta.env) || {};
const WORKOS_AUTH_URL   = ENV.VITE_WORKOS_AUTH_URL   || "";
const WORKOS_CLIENT_ID  = ENV.VITE_WORKOS_CLIENT_ID  || "";
const WORKOS_REDIRECT   = ENV.VITE_WORKOS_REDIRECT_URI || "";

// Local dev (docker mode) authenticates against the Keycloak `dev` realm, not
// WorkOS. When VITE_LOCAL_AUTH_URL is set (only envs/.env.docker sets it),
// bootstrap redirects to the Keycloak authorize endpoint instead of WorkOS; the
// backend redeems the returned code against Keycloak in /v1/auth/exchange using
// the confidential `api-client` secret, so no PKCE is needed here. (O-1634)
const LOCAL_AUTH_URL       = ENV.VITE_LOCAL_AUTH_URL || "";
const LOCAL_AUTH_CLIENT_ID = ENV.VITE_LOCAL_AUTH_CLIENT_ID || "api-client";
// Defense-in-depth: only take the Keycloak path in a dev build. Referencing
// `import.meta.env.DEV` directly lets Vite statically fold it to `false` in
// production builds, so this branch is dead-code-eliminated from the prod bundle
// even if VITE_LOCAL_AUTH_URL ever leaked into a cloud env file. (O-1634)
const USE_LOCAL_AUTH = Boolean(LOCAL_AUTH_URL) && import.meta.env.DEV;

export function usesLocalAuth() {
    return Boolean(USE_LOCAL_AUTH);
}

function safeReplaceUrl(path) {
    try {
        window.history.replaceState({}, document.title, path);
    } catch {}
}

// Deeplink preservation across the WorkOS round-trip.
//
// `VITE_WORKOS_REDIRECT_URI` is a single fixed path carrying no query or
// hash, so a bounce through WorkOS lands the user back on a bare
// `/?code=…` — whatever deeplink they opened (`/?mode=studio&tabs=…`, or
// a `#…` fragment) is gone. Before PT38031_2 (#1343) this was invisible:
// tokens lived in localStorage, so a returning user opening a deeplink
// already had a token at boot and never hit the redirect. Tokens now live
// in the in-process store and don't survive a reload / new tab, so the
// redirect is the common path — and the deeplink was being dropped on
// every cold open.
//
// Fix: stash the landing search+hash right before redirecting, then
// restore it when we strip the `?code=` on the way back, so React mounts
// on the original URL. sessionStorage (not local) so it's tab-scoped and
// survives the navigation out to WorkOS and back.
const DEEPLINK_KEY = "dashboard:auth-deeplink";
const AUTH_DEEPLINK_PARAMS = ["code", "state", "password_reset_token", "reset_auth", "error", "error_description"];

function sanitizeAuthDeeplink(value) {
    const source = String(value || "");
    // Restore only a search/hash suffix on the callback's own pathname. A
    // requested scope is navigation input; authorization stays with the API.
    if (!source.startsWith("?") && !source.startsWith("#")) return "";
    const hashIndex = source.indexOf("#");
    const hash = hashIndex >= 0 ? source.slice(hashIndex) : "";
    const query = source.startsWith("?")
        ? source.slice(1, hashIndex >= 0 ? hashIndex : undefined)
        : "";
    const params = new URLSearchParams(query);
    for (const name of AUTH_DEEPLINK_PARAMS) params.delete(name);
    const safe = params.toString();
    return `${safe ? `?${safe}` : ""}${hash}`;
}

function stashDeeplink() {
    try {
        const deeplink = sanitizeAuthDeeplink(
            `${window.location.search}${window.location.hash}`,
        );
        // A bare `/` (or a lone `?`/`#`) is the default view — nothing to
        // restore, and stashing it would clobber a real deeplink left from
        // an earlier bounce.
        if (deeplink && deeplink !== "?" && deeplink !== "#") {
            sessionStorage.setItem(DEEPLINK_KEY, deeplink);
        } else {
            sessionStorage.removeItem(DEEPLINK_KEY);
        }
    } catch {}
}

// Post-exchange URL: current pathname plus the stashed deeplink (consumed
// — one-shot, so a back-button replay can't re-apply it), falling back to
// the live hash when nothing's stashed. Replaces a bare `pathname + hash`
// at the `?code=` strip so removing the code also re-applies the deeplink
// the WorkOS bounce dropped.
function restoredUrlAfterAuth() {
    let deeplink = "";
    try {
        deeplink = sanitizeAuthDeeplink(sessionStorage.getItem(DEEPLINK_KEY));
        sessionStorage.removeItem(DEEPLINK_KEY);
    } catch {}
    return `${window.location.pathname}${deeplink || window.location.hash}`;
}

// WorkOS multi-org users get `organization_selection_required` back
// from the code exchange. The gateway today bubbles this as a 500
// with the full WorkOS error body serialized inside `.message`. We
// fish the pending state out so the client can show an org picker
// and re-bounce through WorkOS with `organization_id` pinned —
// avoiding a new gateway endpoint. Layout we expect:
//
//   { code, message: "Identity provider error: {\"code\":\"organization_selection_required\", ...}", details: [] }
function parseOrgSelectionFromBody(text) {
    if (!text) return null;
    try {
        const outer = JSON.parse(text);
        const message = outer?.message || outer?.error?.message || "";
        const innerStart = message.indexOf("{");
        if (innerStart < 0) return null;
        const inner = JSON.parse(message.slice(innerStart));
        if (inner?.code !== "organization_selection_required") return null;
        return {
            pendingToken: inner.pending_authentication_token || null,
            user: inner.user || null,
            organizations: Array.isArray(inner.organizations) ? inner.organizations : [],
        };
    } catch {
        return null;
    }
}

const PENDING_ORG_KEY = "dashboard:auth-pending-org-selection";
const PASSWORD_RESET_KEY = "dashboard:auth-password-reset-token";

// Pause after a failed exchange, but do not pin a tab to its error forever.
// A transient failure gets one fresh authorize round-trip per five minutes;
// subsequent failures need an explicit retry to avoid an IdP redirect loop.
const AUTH_EXCHANGE_FAILED_KEY = "dashboard:auth-exchange-failed";
const AUTH_RECOVERY_ATTEMPT_KEY = "dashboard:auth-recovery-attempt";
const AUTH_FAILURE_WINDOW_MS = 5 * 60_000;
const AUTH_RECOVERY_DELAY_MS = 30_000;

function markAuthExchangeFailed(retryable = false) {
    const failure = {
        expiresAt: Date.now() + AUTH_FAILURE_WINDOW_MS,
        retryAt: retryable ? Date.now() + AUTH_RECOVERY_DELAY_MS : null,
    };
    try { sessionStorage.setItem(AUTH_EXCHANGE_FAILED_KEY, JSON.stringify(failure)); } catch {}
    return failure;
}

function readExchangeFailure() {
    try {
        const raw = sessionStorage.getItem(AUTH_EXCHANGE_FAILED_KEY);
        // Upgrade the old, permanent marker so existing stuck tabs can recover.
        if (raw === "1") {
            return markAuthExchangeFailed(true);
        }
        const failure = JSON.parse(raw);
        if (failure?.expiresAt > Date.now()) return failure;
        sessionStorage.removeItem(AUTH_EXCHANGE_FAILED_KEY);
    } catch {}
    return null;
}

export function authExchangeFailed() {
    return readExchangeFailure() !== null;
}

export function clearAuthExchangeFailed() {
    try {
        sessionStorage.removeItem(AUTH_EXCHANGE_FAILED_KEY);
        sessionStorage.removeItem(AUTH_RECOVERY_ATTEMPT_KEY);
    } catch {}
}

function canRetryExchange() {
    if (selfHostedLogin() || getPendingOrgSelection() || getPasswordResetToken()) return false;
    try {
        const previous = sessionStorage.getItem(AUTH_RECOVERY_ATTEMPT_KEY);
        return previous === null || Date.now() - Number(previous) >= AUTH_FAILURE_WINDOW_MS;
    } catch { return false; }
}

export function getAuthExchangeRetryDelay() {
    const failure = readExchangeFailure();
    if (failure?.retryAt == null || !canRetryExchange()) return null;
    return Math.max(0, failure.retryAt - Date.now());
}

export function scheduleAuthExchangeRecovery() {
    const delay = getAuthExchangeRetryDelay();
    if (delay === null) return undefined;
    let expectedFailure;
    try { expectedFailure = sessionStorage.getItem(AUTH_EXCHANGE_FAILED_KEY); } catch { return undefined; }
    const timer = setTimeout(() => {
        if (hasAccessToken() || !canRetryExchange()) return;
        try {
            // A background tab may resume after the failure marker's expiry.
            // Still run its one pending retry, unless the flow changed meanwhile.
            if (sessionStorage.getItem(AUTH_EXCHANGE_FAILED_KEY) !== expectedFailure) return;
            sessionStorage.setItem(AUTH_RECOVERY_ATTEMPT_KEY, String(Date.now()));
            sessionStorage.removeItem(AUTH_EXCHANGE_FAILED_KEY);
            sessionStorage.removeItem(CODE_PROCESSED_KEY);
        } catch { return; }
        // The code may already have been consumed upstream. Obtain a fresh code
        // instead of replaying a possibly successful, single-use exchange.
        if (USE_LOCAL_AUTH) redirectToLocalAuth();
        else redirectToWorkos();
    }, delay);
    return () => clearTimeout(timer);
}

// Explicit sign-in recovery clears every piece of bootstrap state, including
// the automatic retry budget. These keys carry flow state, never bearer tokens.
const BOOTSTRAP_STATE_KEYS = [
    CODE_PROCESSED_KEY,
    DEEPLINK_KEY,
    PENDING_ORG_KEY,
    PASSWORD_RESET_KEY,
    AUTH_EXCHANGE_FAILED_KEY,
    AUTH_RECOVERY_ATTEMPT_KEY,
];

// Escape hatch out of a wedged login. Drops all of this module's
// sessionStorage state so the next bootstrap pass starts from a clean slate
// (no error screen, no stale org picker — just a fresh WorkOS/Keycloak
// redirect). Reached two ways: the `?reset_auth` URL param handled in
// bootstrapAuth() (shareable / support-pasteable, and it works before React
// even mounts), and the "Try sign-in again" button on the login error screen.
export function clearAuthBootstrapState() {
    for (const key of BOOTSTRAP_STATE_KEYS) {
        try { sessionStorage.removeItem(key); } catch {}
    }
}

function writePasswordResetToken(token) {
    try { sessionStorage.setItem(PASSWORD_RESET_KEY, token); } catch {}
}

export function getPasswordResetToken() {
    try { return sessionStorage.getItem(PASSWORD_RESET_KEY); } catch { return null; }
}

export function clearPasswordResetToken() {
    try { sessionStorage.removeItem(PASSWORD_RESET_KEY); } catch {}
}

function writePendingOrgSelection(state) {
    try {
        sessionStorage.setItem(PENDING_ORG_KEY, JSON.stringify(state));
    } catch {}
}

export function getPendingOrgSelection() {
    try {
        const raw = sessionStorage.getItem(PENDING_ORG_KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

export function clearPendingOrgSelection() {
    try { sessionStorage.removeItem(PENDING_ORG_KEY); } catch {}
}

async function exchangeCodeForTokens(code) {
    // Direct hit to the gateway. CORS for `http://localhost:5173` is
    // configured upstream — origin/dashboard does the same.
    const url = `${AUTH_API_BASE}/v1/auth/exchange`;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new DOMException("Sign-in timed out", "TimeoutError")), 15_000);
    try {
        const res = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code, redirect_uri: WORKOS_REDIRECT }),
            signal: controller.signal,
        });
        const text = await res.text();
        controller.signal.throwIfAborted();
        if (!res.ok) {
            const pending = parseOrgSelectionFromBody(text);
            if (pending) {
                writePendingOrgSelection(pending);
                return { __pendingOrgSelection: pending };
            }
            const error = new Error(`auth/exchange ${res.status}: ${text || res.statusText}`);
            error.retryable = res.status >= 500 || res.status === 429;
            throw error;
        }
        try { return JSON.parse(text); } catch { return {}; }
    } finally {
        clearTimeout(timer);
    }
}

// Pre-shim, this was the localStorage writer. Today the fetch shim
// (`authFetchShim.captureAndSanitize`) peels tokens out of the
// `/v1/auth/*` response body into the in-process store before this
// callback runs, so the response `data` we see here has already had
// the token fields stripped. We only need to mirror the `user`
// metadata into the store and report whether the exchange succeeded.
//
// "Succeeded" = the store now holds an access token. The shim
// captures that before sanitizing the body, so a successful 2xx
// response implies `hasAccessToken()` is true here.
export function persistTokens(data) {
    if (data?.user) {
        setTokens({
            user: { authenticationMethod: data.user.authenticationMethod },
        });
    }
    if (hasAccessToken() && data?.user?.email) rememberSignInEmail(data.user.email);
    return hasAccessToken();
}

// The email this browser last signed in with. Tokens don't survive a reload,
// so with it a cold open goes straight back through WorkOS (an SSO session
// returns without a prompt); without it (first visit, or after sign-out)
// LoginPage asks for an email first. Cleared by authBridge.logout().
const LAST_EMAIL_KEY = "dashboard:last-sign-in-email";

export function getLastSignInEmail() {
    try { return localStorage.getItem(LAST_EMAIL_KEY) || ""; } catch { return ""; }
}

function rememberSignInEmail(email) {
    try { localStorage.setItem(LAST_EMAIL_KEY, email); } catch {}
}

export function forgetSignInEmail() {
    try { localStorage.removeItem(LAST_EMAIL_KEY); } catch {}
}

// Build a WorkOS /user_management/authorize URL. `provider` can be:
//   - "authkit"          → WorkOS-hosted picker (default fallback)
//   - "GoogleOAuth"      → straight to Google's consent screen
//   - "MicrosoftOAuth"   → straight to Microsoft's consent screen
//   - "AppleOAuth", "GitHubOAuth", etc.
// Pass `connectionId` instead for a specific SAML/OIDC connection
// (e.g. an enterprise customer's Okta). WorkOS issues an immediate
// 302 to the IdP — its hosted UI never paints.
export function buildAuthorizeUrl({
    provider = "authkit",
    connectionId,
    organizationId,
    loginHint,
} = {}) {
    if (!WORKOS_AUTH_URL || !WORKOS_CLIENT_ID || !WORKOS_REDIRECT) return null;
    const url = new URL(`${WORKOS_AUTH_URL}/user_management/authorize`);
    url.searchParams.set("client_id", WORKOS_CLIENT_ID);
    url.searchParams.set("redirect_uri", WORKOS_REDIRECT);
    url.searchParams.set("response_type", "code");
    if (connectionId) {
        url.searchParams.set("connection", connectionId);
    } else {
        url.searchParams.set("provider", provider);
    }
    if (organizationId) {
        url.searchParams.set("organization_id", organizationId);
    }
    if (loginHint) {
        // AuthKit prefills the email and routes an SSO domain to its IdP.
        url.searchParams.set("login_hint", loginHint);
    }
    return url.toString();
}

export function redirectToWorkos(opts) {
    const url = buildAuthorizeUrl(opts);
    if (!url) {
        // Don't crash — leave the dashboard rendering as-is so the
        // user sees an error instead of a blank page after a botched
        // env. AuthMissingError will fire on the first analytics
        // request and surface a meaningful message.
        console.warn(
            "[authBootstrap] WorkOS env not fully configured; skipping redirect.",
            { WORKOS_AUTH_URL, WORKOS_CLIENT_ID, WORKOS_REDIRECT },
        );
        return;
    }
    // Preserve the deeplink the user opened so the code-exchange leg can
    // restore it — the WorkOS redirect_uri is fixed and carries none of
    // it back.
    stashDeeplink();
    window.location.href = url;
}

// Build the Keycloak authorize URL for local (docker-mode) dev. VITE_LOCAL_AUTH_URL
// is the realm OIDC base (…/realms/dev/protocol/openid-connect); we hit `/auth`,
// the browser lands on the Keycloak login form, and Keycloak returns `?code` to
// redirect_uri. We reuse WORKOS_REDIRECT as the redirect_uri so it matches what
// exchangeCodeForTokens() posts to /v1/auth/exchange — OIDC requires the authorize
// and token-exchange redirect_uri to be identical. (O-1634)
export function buildLocalAuthorizeUrl() {
    if (!LOCAL_AUTH_URL || !WORKOS_REDIRECT) return null;
    const url = new URL(`${LOCAL_AUTH_URL.replace(/\/$/, "")}/auth`);
    url.searchParams.set("client_id", LOCAL_AUTH_CLIENT_ID);
    url.searchParams.set("redirect_uri", WORKOS_REDIRECT);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("scope", "openid email profile");
    return url.toString();
}

export function redirectToLocalAuth() {
    const url = buildLocalAuthorizeUrl();
    if (!url) {
        console.warn(
            "[authBootstrap] VITE_LOCAL_AUTH_URL / redirect not configured; skipping local-auth redirect.",
            { LOCAL_AUTH_URL, WORKOS_REDIRECT },
        );
        return;
    }
    stashDeeplink();
    window.location.href = url;
}

// Resolves to true when auth is ready (either we already have a
// token, or we just exchanged a code for one). Resolves to false when
// auth is required but we couldn't get a token (and we've already
// kicked off a redirect).
//
// React app should `await` this before rendering anything that
// triggers an API call — though in practice the analytics handlers
// throw AuthMissingError if they fire pre-token, which is also fine.
let _bootstrapPromise = null;

export function bootstrapAuth() {
    if (_bootstrapPromise) return _bootstrapPromise;
    _bootstrapPromise = (async () => {
        if (!AUTH_REQUIRED) return true;

        const params = new URLSearchParams(window.location.search);

        // Escape hatch for a wedged login (O-1321 follow-up). Appending
        // `?reset_auth` (any/no value) drops all bootstrap state so this pass
        // can't land back on the error screen / stale org picker — it falls
        // through to a clean redirect. Handled first, and the param is
        // stripped immediately, so a reload or a shared link can't re-trigger
        // it and a returning `?code=` is still processed normally below.
        if (params.has("reset_auth")) {
            clearAuthBootstrapState();
            params.delete("reset_auth");
            const qs = params.toString();
            safeReplaceUrl(
                window.location.pathname +
                    (qs ? `?${qs}` : "") +
                    window.location.hash,
            );
        }

        const code = params.get("code");
        const resetToken = params.get("password_reset_token");

        if (resetToken) {
            // WorkOS-style password-reset link landing. Stash the token
            // and strip it from the URL so a refresh / share doesn't
            // leak it. LoginPage picks it up and renders a "set new
            // password" screen.
            writePasswordResetToken(resetToken);
            safeReplaceUrl(window.location.pathname + window.location.hash);
        }

        if (code) {
            // Single-use guard — sessionStorage so it doesn't survive a
            // tab close, but does survive React StrictMode's double-mount.
            const alreadyProcessed = sessionStorage.getItem(CODE_PROCESSED_KEY) === code;
            if (!alreadyProcessed) {
                sessionStorage.setItem(CODE_PROCESSED_KEY, code);
                // Strip code from URL immediately so a re-render or
                // back-button can't trigger a second exchange, and restore
                // the deeplink the WorkOS bounce dropped.
                safeReplaceUrl(restoredUrlAfterAuth());
                try {
                    const data = await exchangeCodeForTokens(code);
                    if (data?.__pendingOrgSelection) {
                        // pending state already stashed in sessionStorage;
                        // fall through to render the picker. Not a failure.
                    } else if (persistTokens(data)) {
                        clearAuthExchangeFailed();
                        return true;
                    } else {
                        // A malformed or interrupted response may hide a used
                        // code. Allow only the same bounded fresh-code recovery.
                        markAuthExchangeFailed(true);
                    }
                } catch (err) {
                    console.error("[authBootstrap] code exchange failed:", err);
                    markAuthExchangeFailed(err?.retryable ?? (err instanceof TypeError || err?.name === "TimeoutError" || err?.name === "AbortError"));
                }
            } else {
                safeReplaceUrl(restoredUrlAfterAuth());
            }
        }

        if (hasAccessToken()) return true;

        // No token, no usable code. We render <LoginPage /> instead of
        // redirecting in four cases:
        //   - the self-hosted-login flag is on, OR
        //   - there's a pending multi-org selection from a prior code
        //     exchange (the page renders an org picker), OR
        //   - the user just landed via a password-reset email link
        //     (the page renders a "set new password" screen), OR
        //   - a code exchange just failed this session — the circuit
        //     breaker (O-1321); redirecting again would mint a fresh code
        //     and loop into the WorkOS rate limit.
        // Otherwise a returning browser goes back through WorkOS, and a new
        // one gets the email-first LoginPage.
        if (
            selfHostedLogin() ||
            getPendingOrgSelection() ||
            getPasswordResetToken() ||
            authExchangeFailed()
        ) return false;

        // Docker/local DEV builds authenticate against Keycloak; cloud builds use WorkOS.
        if (USE_LOCAL_AUTH) {
            redirectToLocalAuth();
        } else if (getLastSignInEmail()) {
            redirectToWorkos({ loginHint: getLastSignInEmail() });
        }
        return false;
    })();
    return _bootstrapPromise;
}

// True when bootstrap declined to authenticate AND we have something
// for the LoginPage to actually render — the self-hosted login flag,
// a pending multi-org selection, a password-reset link landing, or a
// just-failed code exchange (O-1321: render the login/error screen
// rather than a blank page or another redirect loop).
export function shouldRenderSelfHostedLogin() {
    if (!AUTH_REQUIRED) return false;
    if (hasAccessToken()) return false;
    return (
        selfHostedLogin() ||
        !!getPendingOrgSelection() ||
        !!getPasswordResetToken() ||
        authExchangeFailed() ||
        (!USE_LOCAL_AUTH && !getLastSignInEmail())
    );
}
